(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 761px)');
  let lightbox;
  let lightboxImage;
  let lightboxViewport;
  let lightboxCanvas;
  let lightboxZoom;
  let isZoomed = false;
  let returnFocus;
  let previousOverflow;

  function setZoom(zoom, point) {
    if (!lightboxImage || !lightboxViewport) return;
    const bounds = lightboxImage.getBoundingClientRect();
    if (zoom && (!bounds.width || !bounds.height)) return;
    isZoomed = zoom;
    lightbox.classList.toggle('is-zoomed', zoom);
    lightboxZoom.textContent = zoom ? 'Fit image' : 'Zoom in';
    lightboxZoom.setAttribute('aria-pressed', String(zoom));
    lightboxZoom.setAttribute('aria-label', zoom ? 'Fit entire dress in view' : 'Zoom in to view dress details');
    if (zoom) {
      const width = Math.round(bounds.width * 2);
      const height = Math.round(bounds.height * 2);
      lightboxImage.style.width = `${width}px`;
      lightboxImage.style.height = `${height}px`;
      lightboxCanvas.style.width = `${width + 32}px`;
      lightboxCanvas.style.height = `${height + 32}px`;
      const x = point ? (point.x - bounds.left) / bounds.width : 0.5;
      const y = point ? (point.y - bounds.top) / bounds.height : 0.5;
      lightboxViewport.scrollTo({
        left: Math.max(0, x * width + 16 - lightboxViewport.clientWidth / 2),
        top: Math.max(0, y * height + 16 - lightboxViewport.clientHeight / 2),
        behavior: 'auto'
      });
    } else {
      lightboxImage.style.width = '';
      lightboxImage.style.height = '';
      lightboxCanvas.style.width = '';
      lightboxCanvas.style.height = '';
      lightboxViewport.scrollTo({ left: 0, top: 0, behavior: 'auto' });
    }
  }

  function getLightbox() {
    if (lightbox) return lightbox;
    if (typeof HTMLDialogElement === 'undefined') return null;

    lightbox = document.createElement('dialog');
    lightbox.className = 'pdp-image-dialog';
    lightbox.setAttribute('aria-label', 'Product image preview');
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'pdp-image-dialog__close';
    close.setAttribute('aria-label', 'Close image preview');
    close.textContent = '×';
    lightboxZoom = document.createElement('button');
    lightboxZoom.type = 'button';
    lightboxZoom.className = 'pdp-image-dialog__zoom';
    lightboxZoom.textContent = 'Zoom in';
    lightboxZoom.setAttribute('aria-label', 'Zoom in to view dress details');
    lightboxZoom.setAttribute('aria-pressed', 'false');
    lightboxZoom.setAttribute('aria-controls', 'maria-product-zoom-image');
    lightboxViewport = document.createElement('div');
    lightboxViewport.className = 'pdp-image-dialog__viewport';
    lightboxViewport.tabIndex = 0;
    lightboxViewport.setAttribute('role', 'region');
    lightboxViewport.setAttribute('aria-label', 'Dress photograph. Zoom in, then scroll to explore details.');
    lightboxCanvas = document.createElement('div');
    lightboxCanvas.className = 'pdp-image-dialog__canvas';
    lightboxImage = document.createElement('img');
    lightboxImage.id = 'maria-product-zoom-image';
    lightboxImage.className = 'pdp-image-dialog__image';
    lightboxImage.alt = '';
    lightboxImage.draggable = false;
    lightboxCanvas.append(lightboxImage);
    lightboxViewport.append(lightboxCanvas);
    lightbox.append(close, lightboxZoom, lightboxViewport);
    document.body.append(lightbox);

    close.addEventListener('click', () => lightbox.close());
    lightboxZoom.addEventListener('click', () => setZoom(!isZoomed));
    lightboxImage.addEventListener('click', (event) => setZoom(!isZoomed, { x: event.clientX, y: event.clientY }));
    lightbox.addEventListener('click', (event) => {
      if (event.target === lightbox) lightbox.close();
    });
    lightbox.addEventListener('close', () => {
      setZoom(false);
      if (previousOverflow !== undefined) {
        document.body.style.overflow = previousOverflow;
        previousOverflow = undefined;
      }
      if (returnFocus && returnFocus.isConnected) returnFocus.focus({ preventScroll: true });
      returnFocus = null;
    });
    return lightbox;
  }

  function openImage(button) {
    const dialog = getLightbox();
    const image = button.querySelector('img');
    if (!dialog || !image || dialog.open) return;
    const source = button.dataset.zoomSrc || image.currentSrc || image.src;
    if (!source) return;
    lightboxImage.src = source;
    lightboxImage.alt = image.alt || 'Detailed view of this dress';
    returnFocus = button;
    previousOverflow = document.body.style.overflow;
    dialog.showModal();
    document.body.style.overflow = 'hidden';
  }

  function pauseMedia(figure) {
    figure.querySelectorAll('video').forEach((video) => video.pause());
    figure.querySelectorAll('iframe').forEach((frame) => {
      if (!frame.contentWindow || !frame.src) return;
      try {
        const origin = new URL(frame.src, window.location.href).origin;
        if (/^https:\/\/(www\.)?youtube(-nocookie)?\.com$/.test(origin)) {
          frame.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), origin);
        } else if (origin === 'https://player.vimeo.com') {
          frame.contentWindow.postMessage(JSON.stringify({ method: 'pause' }), origin);
        }
      } catch (_) {
        // An unavailable external player must not interrupt gallery navigation.
      }
    });
  }

  function enhanceGallery(gallery) {
    if (gallery.classList.contains('is-enhanced')) return;
    const stage = gallery.querySelector('[data-gallery-stage]');
    if (!stage) return;
    const items = Array.from(stage.querySelectorAll('.pdp-gallery__item'));
    if (!items.length) return;
    const thumbs = gallery.querySelector('[data-gallery-thumbs]');
    const thumbButtons = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
    const controls = gallery.querySelector('.pdp-gallery__controls');
    const count = gallery.querySelector('[data-gallery-count]');
    const previous = gallery.querySelector('[data-gallery-prev]');
    const next = gallery.querySelector('[data-gallery-next]');
    let current = 0;
    let suppressZoomUntil = 0;
    let pointerStart = null;
    let scrollFrame = null;
    let settling = null;

    function render() {
      items.forEach((item, index) => {
        const visible = !desktop.matches || index === current;
        if (!visible && !item.hidden) pauseMedia(item);
        item.hidden = !visible;
        item.style.order = '';
      });
      const activeId = String(items[current].dataset.mediaId);
      thumbButtons.forEach((button) => {
        const active = String(button.dataset.galleryThumb) === activeId;
        button.setAttribute('aria-pressed', String(active));
      });
      if (count) {
        count.textContent = `${current + 1} / ${items.length}`;
        count.setAttribute('aria-label', `Image ${current + 1} of ${items.length}`);
      }
    }

    function show(index, behavior) {
      const target = (index + items.length) % items.length;
      if (current !== target) pauseMedia(items[current]);
      current = target;
      render();
      if (desktop.matches && thumbs) {
        const activeThumb = thumbButtons.find((button) => String(button.dataset.galleryThumb) === String(items[current].dataset.mediaId));
        if (activeThumb) {
          const offset = activeThumb.offsetTop - thumbs.offsetTop;
          if (offset < thumbs.scrollTop || offset + activeThumb.offsetHeight > thumbs.scrollTop + thumbs.clientHeight) {
            thumbs.scrollTo({ top: Math.max(0, offset - (thumbs.clientHeight - activeThumb.offsetHeight) / 2), behavior: behavior || (reducedMotion.matches ? 'auto' : 'smooth') });
          }
        }
      }
      if (!desktop.matches) {
        stage.scrollTo({
          left: items[current].offsetLeft - items[0].offsetLeft,
          behavior: behavior || (reducedMotion.matches ? 'auto' : 'smooth')
        });
      }
    }

    function syncScroll() {
      scrollFrame = null;
      if (desktop.matches) return;
      const left = stage.getBoundingClientRect().left;
      let closest = 0;
      let distance = Infinity;
      items.forEach((item, index) => {
        const delta = Math.abs(item.getBoundingClientRect().left - left);
        if (delta < distance) { closest = index; distance = delta; }
      });
      if (current !== closest) {
        pauseMedia(items[current]);
        current = closest;
        render();
      }
    }

    // Native scrolling keeps photos attached to the finger and permits pinch zoom.
    stage.addEventListener('scroll', () => {
      if (desktop.matches) return;
      suppressZoomUntil = Date.now() + 180;
      if (!scrollFrame) scrollFrame = requestAnimationFrame(syncScroll);
      clearTimeout(settling);
      settling = setTimeout(syncScroll, 120);
    }, { passive: true });

    thumbButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const index = items.findIndex((item) => String(item.dataset.mediaId) === String(button.dataset.galleryThumb));
        if (index !== -1) show(index);
      });
    });
    if (previous) previous.addEventListener('click', () => show(current - 1));
    if (next) next.addEventListener('click', () => show(current + 1));

    gallery.querySelectorAll('[data-image-zoom]').forEach((button) => {
      const image = button.querySelector('img');
      if (image) image.draggable = false;
      if (typeof HTMLDialogElement === 'undefined') return;
      button.disabled = false;
      if (!button.getAttribute('aria-label')) button.setAttribute('aria-label', 'Open larger image');
      button.setAttribute('aria-haspopup', 'dialog');
      button.addEventListener('click', (event) => {
        if (Date.now() < suppressZoomUntil) { event.preventDefault(); return; }
        openImage(button);
      });
    });

    stage.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary) { pointerStart = null; suppressZoomUntil = Date.now() + 400; return; }
      pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
    }, { passive: true });
    stage.addEventListener('pointerup', (event) => {
      if (!pointerStart || pointerStart.id !== event.pointerId) return;
      if (Math.hypot(event.clientX - pointerStart.x, event.clientY - pointerStart.y) > 12) {
        suppressZoomUntil = Date.now() + 400;
      }
      pointerStart = null;
    }, { passive: true });
    stage.addEventListener('pointercancel', () => {
      pointerStart = null;
      suppressZoomUntil = Date.now() + 400;
    }, { passive: true });

    gallery.addEventListener('keydown', (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (!event.target.closest('[data-gallery-thumb], [data-gallery-prev], [data-gallery-next], [data-image-zoom]')) return;
      const focusedItem = event.target.closest('.pdp-gallery__item');
      const focusedIndex = focusedItem ? items.indexOf(focusedItem) : current;
      const movement = { ArrowLeft: focusedIndex - 1, ArrowRight: focusedIndex + 1, Home: 0, End: items.length - 1 };
      if (!(event.key in movement)) return;
      event.preventDefault();
      const moveFocus = Boolean(event.target.closest('[data-image-zoom]'));
      show(movement[event.key]);
      if (moveFocus) {
        const trigger = items[current].querySelector('[data-image-zoom]');
        if (trigger) trigger.focus({ preventScroll: true });
        else if (next) next.focus({ preventScroll: true });
      }
    });

    document.addEventListener('maria:variant-change', (event) => {
      const variant = event.detail && event.detail.variant;
      const mediaId = variant && ((variant.featured_media && variant.featured_media.id) || (variant.featured_image && variant.featured_image.id));
      if (!mediaId) return;
      const index = items.findIndex((item) => String(item.dataset.mediaId) === String(mediaId));
      if (index !== -1) show(index);
    });

    gallery.classList.add('is-enhanced');
    gallery.classList.toggle('has-single-item', items.length === 1);
    if (controls) controls.hidden = items.length < 2;
    if (thumbs) thumbs.hidden = items.length < 2;
    desktop.addEventListener('change', () => show(current, 'auto'));
    show(0, 'auto');
  }

  function init(root = document) {
    root.querySelectorAll('[data-gallery]').forEach(enhanceGallery);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
